import HomeIcon from "@mui/icons-material/Home";
import PersonIcon from "@mui/icons-material/Person";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import { blueGrey, teal } from "@mui/material/colors";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../../services/AuthService";
import Logger from "../../services/Logger";
import UserService from "../../services/UserService";
import { useAdminLanguage } from "../../context/AdminLanguageContext";

function AdminTopBar({ open }) {
    const navigate = useNavigate();
    const { language, setLanguage } = useAdminLanguage();
    const [anchorElUser, setAnchorElUser] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [settings, setSettings] = useState([]);

    useEffect(() => {
        const checkAuthAndLoadUser = async () => {
            try {
                const authStatus = await AuthService.isAuthenticated();

                if (authStatus) {
                    const u = await UserService.getUserInfo();
                    setUserInfo(u.cloudUser);
                    setSettings([{ text: "Sign out", icon: null, action: "signOut" }]);
                } else {
                    setUserInfo(null);
                    setSettings([{ text: "Sign in", icon: null, action: "signIn" }]);
                }
            } catch (error) {
                Logger.error('Error checking auth or loading user:', error);
                setUserInfo(null);
                setSettings([{ text: "Sign in", icon: null, action: "signIn" }]);
            }
        };

        checkAuthAndLoadUser();
    }, []);

    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorElUser(null);
    };

    const handleMenu = async (action) => {
        switch (action) {
            case "signOut":
                try {
                    await AuthService.logout();
                    setUserInfo(null);
                    window.location.href = "/";
                } catch (error) {
                    Logger.error('Logout error:', error);
                }
                setAnchorElUser(null);
                break;
            case "signIn":
                try {
                    await AuthService.login();
                } catch (error) {
                    Logger.error('Login error:', error);
                }
                setAnchorElUser(null);
                break;
            default:
                break;
        }
    };

    return (
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", position: "relative" }}>
            {/* Left: Site Name */}
            <Typography
                variant="h6"
                noWrap
                component="a"
                href="/"
                sx={{
                    display: { xs: "none", md: "flex" },
                    fontFamily: "monospace",
                    fontWeight: 700,
                    letterSpacing: ".3rem",
                    color: "inherit",
                    textDecoration: "none",
                    width: { xs: 0, md: open ? 0 : 'auto' },
                    overflow: 'hidden',
                    transition: 'width 0.2s ease-in-out',
                    mr: { xs: 0, md: open ? 0 : 2 },
                }}
            >
                {process.env.REACT_APP_SITE_NAME}
            </Typography>

            {/* Center: Language Switch */}
            <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: { xs: "absolute", md: "absolute" },
                left: { xs: "50%", md: "50%" },
                transform: { xs: "translateX(-50%)", md: "translateX(-50%)" },
                width: 'fit-content',
            }}>
                <ToggleButtonGroup
                    value={language}
                    exclusive
                    onChange={(_event, newLanguage) => {
                        if (newLanguage !== null) {
                            setLanguage(newLanguage);
                            // Reload the page to apply language change
                            window.location.reload();
                        }
                    }}
                    size="small"
                    sx={{
                        backgroundColor: 'transparent',
                        border: '1px solid white',
                        '& .MuiToggleButton-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                            backgroundColor: 'transparent',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                            borderLeft: 'none',
                            minWidth: 60,
                            width: 60,
                            px: 2,
                            py: 0.75,
                            transition: 'all 0.2s ease-in-out',
                            '&:first-of-type': {
                                borderLeft: '1px solid rgba(255, 255, 255, 0.3)',
                            },
                            '&.Mui-selected': {
                                backgroundColor: '#FF9800',
                                color: 'white',
                                borderColor: '#FF9800',
                                borderLeft: 'none',
                                fontWeight: 600,
                                fontSize: '1.05rem',
                                boxShadow: '0 2px 8px rgba(255, 152, 0, 0.4)',
                                '& .MuiTypography-root': {
                                    fontSize: '1.05rem',
                                    fontWeight: 600,
                                },
                                '&:first-of-type': {
                                    borderLeft: '1px solid #FF9800',
                                },
                                '&:hover': {
                                    backgroundColor: '#FB8C00',
                                    borderColor: '#FB8C00',
                                    borderLeft: 'none',
                                    '&:first-of-type': {
                                        borderLeft: '1px solid #FB8C00',
                                    },
                                }
                            },
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                color: 'rgba(255, 255, 255, 0.9)',
                                borderColor: 'rgba(255, 255, 255, 0.5)',
                            }
                        }
                    }}
                >
                    <ToggleButton value="english">
                        <Typography variant="body2" sx={{ px: 1 }}>
                            EN
                        </Typography>
                    </ToggleButton>
                    <ToggleButton value="french">
                        <Typography variant="body2" sx={{ px: 1 }}>
                            FR
                        </Typography>
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Right: User Menu and Home Button */}
            <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "flex-end", 
                gap: 1, 
                ml: "auto",
            }}>
                {/* User Menu */}
                <Tooltip title="Open settings">
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                        <Avatar sx={{ bgcolor: teal[200] }}>
                            <PersonIcon />
                        </Avatar>
                    </IconButton>
                </Tooltip>

                {/* Home Button */}
                <Tooltip title="Go to Home Page" arrow>
                    <IconButton onClick={() => navigate("/")} sx={{ p: 0 }}>
                        <Avatar sx={{ bgcolor: teal[200] }}>
                            <HomeIcon />
                        </Avatar>
                    </IconButton>
                </Tooltip>
                <Menu
                    sx={{ mt: "45px" }}
                    id="menu-appbar"
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    keepMounted
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    open={Boolean(anchorElUser)}
                    onClose={handleClose}
                    disableScrollLock
                >
                    {userInfo && (
                        <MenuItem key={userInfo.email} divider>
                            <div>
                                <Typography textAlign="left" variant="subtitle1">
                                    {userInfo.firstName} {userInfo.lastName}
                                </Typography>
                                <Typography textAlign="left" variant="caption">
                                    {userInfo.email}
                                </Typography>
                            </div>
                        </MenuItem>
                    )}

                    {settings.map((setting) => (
                        <MenuItem
                            key={setting.text}
                            onClick={() => handleMenu(setting.action)}
                            dense
                            sx={{
                                "&:hover": {
                                    backgroundColor: blueGrey[100],
                                },
                            }}
                        >
                            <Typography textAlign="center">{setting.text}</Typography>
                        </MenuItem>
                    ))}
                </Menu>
            </Box>
        </Box>
    );
}

export default AdminTopBar;
